import { z } from 'zod/v3';
import { analyticsBucketSchema, analyticsOverviewSchema } from './schemas';

/**
 * Backstage-owned envelope around the unchanged upstream overview. `fetchedAt`
 * is when Backstage successfully received and validated the upstream payload,
 * not a timestamp supplied by agentic-review.
 */
export const analyticsSnapshotSchema = z
  .object({
    overview: analyticsOverviewSchema,
    fetchedAt: z.string().datetime(),
  })
  .strict();

/** A cache miss is `snapshot: null`; a zero-runs overview is a valid snapshot. */
export const analyticsCacheReadResponseSchema = z
  .object({ snapshot: analyticsSnapshotSchema.nullable() })
  .strict();

function normalizeRepository(repo: string | undefined): string | undefined {
  return repo?.trim() || undefined;
}

/**
 * Query shared by the cache-read and fresh routes. Query strings carry
 * `workdaysOnly` as text, so it is decoded explicitly: boolean coercion would
 * turn `"false"` into true. Upstream stays authoritative for bucket limits.
 */
export const analyticsOverviewRequestSchema = z
  .object({
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    bucket: analyticsBucketSchema,
    repo: z.string().optional(),
    timeZone: z.enum(['UTC', 'Europe/Prague']).optional(),
    workdaysOnly: z.enum(['true', 'false']).optional(),
  })
  .superRefine((query, context) => {
    if (Date.parse(query.from) >= Date.parse(query.to)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'from must be before to',
      });
    }
    if (query.workdaysOnly === 'true' && query.timeZone === 'UTC') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['workdaysOnly'],
        message: 'Czech workday charts require Europe/Prague timeZone',
      });
    }
  })
  .transform(({ repo, workdaysOnly, ...query }) => {
    const trimmedRepo = normalizeRepository(repo);
    return {
      ...query,
      ...(trimmedRepo ? { repo: trimmedRepo } : {}),
      ...(workdaysOnly !== undefined
        ? { workdaysOnly: workdaysOnly === 'true' }
        : {}),
    };
  });

/** Exact scope identity; absent options must not collapse into explicit defaults. */
export function analyticsScopeTuple(request: AnalyticsOverviewRequest) {
  return [
    request.from,
    request.to,
    request.bucket,
    normalizeRepository(request.repo) ?? null,
    request.timeZone ?? null,
    request.workdaysOnly ?? null,
  ] as const;
}

export type AnalyticsSnapshot = z.infer<typeof analyticsSnapshotSchema>;
export type AnalyticsCacheReadResponse = z.infer<
  typeof analyticsCacheReadResponseSchema
>;
export type AnalyticsOverviewRequest = z.infer<
  typeof analyticsOverviewRequestSchema
>;
